import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authApi } from '../../api/auth.api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  status: string;
  kycStatus: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const storedUser = localStorage.getItem('airgo_user');
const storedToken = localStorage.getItem('airgo_access_token');

const initialState: AuthState = {
  user: storedUser ? JSON.parse(storedUser) : null,
  accessToken: storedToken,
  refreshToken: localStorage.getItem('airgo_refresh_token'),
  isAuthenticated: !!storedToken && !!storedUser,
  isLoading: false,
  error: null,
};

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authApi.login(credentials.email, credentials.password);
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      return rejectWithValue(err.response?.data?.error || 'Login failed');
    }
  },
);

export const register = createAsyncThunk(
  'auth/register',
  async (data: Record<string, string>, { rejectWithValue }) => {
    try {
      const response = await authApi.register(data as never);
      return response.data;
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string; errors?: { field: string; message: string }[] } } };
      const apiErrors = err.response?.data?.errors;
      if (apiErrors?.length) {
        // Show first specific field error so the user knows exactly what to fix
        return rejectWithValue(apiErrors.map((e) => e.message).join(', '));
      }
      return rejectWithValue(err.response?.data?.error || 'Registration failed');
    }
  },
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState };
    if (state.auth.refreshToken) {
      await authApi.logout(state.auth.refreshToken).catch(() => {});
    }
  },
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTokens(state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      localStorage.setItem('airgo_access_token', action.payload.accessToken);
      localStorage.setItem('airgo_refresh_token', action.payload.refreshToken);
    },
    clearError(state) {
      state.error = null;
    },
    updateUser(state, action: PayloadAction<Partial<User>>) {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('airgo_user', JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        // API response shape: { success, data: { accessToken, refreshToken, user } }
        const payload = action.payload?.data ?? action.payload;
        state.isLoading = false;
        state.user = payload.user;
        state.accessToken = payload.accessToken;
        state.refreshToken = payload.refreshToken;
        state.isAuthenticated = true;
        localStorage.setItem('airgo_user', JSON.stringify(payload.user));
        localStorage.setItem('airgo_access_token', payload.accessToken);
        localStorage.setItem('airgo_refresh_token', payload.refreshToken);
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        localStorage.removeItem('airgo_user');
        localStorage.removeItem('airgo_access_token');
        localStorage.removeItem('airgo_refresh_token');
      });
  },
});

export const { setTokens, clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;
